import React, { useEffect, useMemo } from 'react';
import { BsPlus, BsStar, BsStarFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, Image, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../../../constants/inAppText';
import { Statuses } from '../../../../../../../../constants/statuses';
import { TabNames } from '../../../../../../../../constants/tabMenuTypes';
import emptyBox from '../../../../../../../../empty-box.png';
import { hideTray, showTray } from '../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import SearchInputGroup from '../../../../../../../SearchInputGroup';
import { customLanguageOptions, REQUIREMENT_TYPES } from '../../../../../../util/options';
import { getCreateRequirementTemplateBlockParams } from '../../../../../../util/util';
import { RequirementMenuItem, RequirementPropertyTypeMenu } from '../../../../../RequirementPropertyTypeMenu';
import RequirementTypesFilters from '../../../../../RequirementTypesFilters';

import CreateCustomLanguageTray from './components/CreateCustomLanguageTray';

const EmptyPanel = () => (
  <Flex
    alignItems="center"
    borderTop={`1px solid ${colors.greyScale['200']}`}
    flexDirection="column"
    justifyContent="center"
    px={6}
    py={5}
  >
    <Image height="auto" src={emptyBox} width="160px" />
    <Text color={colors.greyScale['500']} pt="2rem" textStyle="bodyLg500">
      No Saved Languages
    </Text>
    <Text color={colors.greyScale['500']} textStyle="bodyMd">
      Create custom language above and save it to this list to use again later.
    </Text>
  </Flex>
);

const TemplateBuilderLanguagePanel = ({ blueprint }) => {
  const dispatch = useDispatch();

  const currentTemplate = useSelector(blueprint.selectCurrentRequirementTemplate);
  const currentRequirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);
  let currentMaxOrder = 0;

  useEffect(() => {
    if (currentRequirementTemplateBlocks) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentMaxOrder = Math.max(...currentRequirementTemplateBlocks.map(templateBlock => templateBlock.order));
    }
  }, [currentRequirementTemplateBlocks, dispatch]);

  const insuranceLanguages = useSelector(blueprint.selectInsuranceLanguages);

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    dispatch(blueprint.updateInsuranceLanguageSearchFilter(searchValue));
  };

  const handleFilterChange = (isChecked, type) => {
    dispatch(blueprint.setInsuranceLanguageFilters({ ...insuranceLanguages.filters, [type]: isChecked }));
  };

  const showCreateCustomLanguageTray = () => {
    dispatch(blueprint.setCreateCustomStep(0, TabNames.Language));
    dispatch(showTray(
      () => <CreateCustomLanguageTray blueprint={blueprint} />,
      {
        onClose: () => {
          dispatch(hideTray());
        }
      }
    ));
  };

  const insuranceLanguageTabs = useMemo(() => insuranceLanguages?.data.map(languageData => ({
    id: languageData?.id,
    tabId: languageData?.name,
    title: languageData?.displayName,
    description: languageData?.language,
    icon: languageData?.favorite ? BsStarFill : BsStar,
    isFavorite: languageData?.favorite ?? 0,
    onClick: () => {
      if (currentTemplate?.publishedAt) dispatch(blueprint.updateChangesPublishedStatus(Statuses.Updating));
      dispatch(blueprint.createRequirementTemplateBlock(getCreateRequirementTemplateBlockParams(
        currentMaxOrder,
        REQUIREMENT_TYPES.Language,
        languageData
      )));
    },
    onFavoriteButtonCLick: () => (languageData?.favorite
      ? dispatch(blueprint.removeLanguageFavorite(languageData?.id))
      : dispatch(blueprint.addLanguageFavorite(languageData?.id))),
    submenu: languageData?.companyId ? customLanguageOptions : null
  })), [currentMaxOrder, dispatch, insuranceLanguages.data]);

  const pageData = insuranceLanguages?.pageInfo
    ? { pageNumber: (insuranceLanguages.pageInfo?.currentPage ?? 0) + 1 } : null;

  return (
    <Flex direction="column" h="100%" justify="flex-start" w="100%">
      <Box mt={4} px={5} py={4}>
        <Text color={colors.greyScale['500']} textAlign="left" textStyle="bodySm">
          {inAppText.templatesPage.templateBuilder.languagePane.addLanguageInstructions}
        </Text>
        <Button
          leftIcon={<Icon as={BsPlus} />}
          mt={4}
          variant="secondary"
          w="100%"
          onClick={showCreateCustomLanguageTray}
        >
          Create Custom Language
        </Button>
        <HStack justify="space-around" mt={4} spacing={1} width="100%">
          <SearchInputGroup h="40px" onChange={handleSearchChange} />
          <RequirementTypesFilters
            blueprint={blueprint}
            currentFilters={insuranceLanguages.filters}
            type="Language"
            onChange={handleFilterChange}
            onReset={() => dispatch(blueprint.listInsuranceLanguages())}
          />
        </HStack>
      </Box>
      {insuranceLanguageTabs?.length > 0
        ? (
          <RequirementPropertyTypeMenu
            blueprint={blueprint}
            cypressIdFunction={CypressIds.languageTypeMenuOption}
            fetchNext={() => dispatch(blueprint.listInsuranceLanguages({ pageNumber: insuranceLanguages.currentPage + 1, pageSize: 25 }))}
            hasMore={insuranceLanguages?.currentPage !== insuranceLanguages?.lastPage}
          >
            {insuranceLanguageTabs.map(option => (
              <RequirementMenuItem
                {...option}
                key={option.id}
                subMenu={option?.submenu}
              />
            ))}
          </RequirementPropertyTypeMenu>
        ) : <EmptyPanel />}
    </Flex>
  );
};

TemplateBuilderLanguagePanel.propTypes = { blueprint: RequirementTemplateBlueprintType };

export default TemplateBuilderLanguagePanel;
